import { useState, useCallback, useEffect, useMemo } from 'react';
import { teamService } from '../services/teamService';

const formatLocalYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const useTeams = () => {
  const [showDetails, setShowDetails] = useState(false);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [viewingEmployee, setViewingEmployee] = useState(null);

  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({
    totalMembers: 0,
    activePercentage: 0,
    roleCount: 0,
    locationCount: 0
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('All Roles');
  const [installationTradeFilter, setInstallationTradeFilter] = useState('All trades');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [datePreset, setDatePreset] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  // Debounce search query only
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchTeams = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = {
        page,
        limit: 10,
        search: debouncedSearchQuery,
        role: roleFilter,
        installationTrade: installationTradeFilter
      };

      let from = dateRange.from;
      let to = dateRange.to;

      // Calculate dates dynamically if a preset is active
      if (datePreset) {
        const now = new Date();
        const todayStr = formatLocalYMD(now);
        to = todayStr;

        if (datePreset === 'today') {
          from = todayStr;
        } else if (datePreset === 'this_week') {
          const start = new Date(now);
          start.setDate(start.getDate() - 6);
          from = formatLocalYMD(start);
        } else if (datePreset === 'this_month') {
          const start = new Date(now);
          start.setDate(start.getDate() - 29);
          from = formatLocalYMD(start);
        } else if (datePreset === 'this_year') {
          const start = new Date(now);
          start.setDate(start.getDate() - 364);
          from = formatLocalYMD(start);
        }
      }

      if (from) params.startDate = from;
      if (to) params.endDate = to;

      const response = await teamService.getEmployees(params);

      setEmployees(response?.data || []);
      setTotalPages(response?.pagination?.totalPages || 1);
      setTotalItems(response?.pagination?.totalItems || 0);
      if (response?.stats) setStats(response.stats);
    } catch (err) {
      setError(err);
      console.error('Failed to fetch teams:', err);
    } finally {
      setIsLoading(false);
    }
  }, [page, debouncedSearchQuery, roleFilter, installationTradeFilter, dateRange.from, dateRange.to, datePreset]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setPage(1);
  }, []);

  const handleRoleFilter = useCallback((role) => {
    setRoleFilter(role);
    setPage(1);
  }, []);

  const handleInstallationTradeFilter = useCallback((value) => {
    setInstallationTradeFilter(value);
    setPage(1);
  }, []);

  const handleStartDateChange = useCallback((value) => {
    setDatePreset(null);
    setDateRange((prev) => ({ ...prev, from: value }));
    setPage(1);
  }, []);

  const handleEndDateChange = useCallback((value) => {
    setDatePreset(null);
    setDateRange((prev) => ({ ...prev, to: value }));
    setPage(1);
  }, []);

  // Polinaidu: 22-04-2026 (16:46) - Fixed null preset handling to allow clearing dates without defaulting to Today
  const handleDatePreset = useCallback((preset) => {
    if (preset === 'all' || !preset) {
      setDatePreset(null);
    } else {
      setDatePreset(preset);
      // Clear manual range when choosing a preset
      setDateRange({ from: '', to: '' });
    }
    setPage(1);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const openCreateModal = useCallback(() => {
    setEditingEmployee(null);
    setTeamModalOpen(true);
  }, []);

  const openEditEmployee = useCallback((emp) => {
    setEditingEmployee(emp);
    setTeamModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setTeamModalOpen(false);
    setEditingEmployee(null);
  }, []);

  const handleEmployeeSubmit = async (data, { onSuccess, setError } = {}) => {
    setIsLoading(true);
    setError(null);
    try {
      const payload = { ...data };
      if (payload.role !== 'field_officer') {
        delete payload.installationTrade;
      }
      if (editingEmployee?._id) {
        if (!payload.password) delete payload.password;
        await teamService.updateEmployee(editingEmployee._id, payload);
        setToast({ message: 'Team member updated successfully.', type: 'success' });
      } else {
        await teamService.createEmployee(payload);
        setToast({ message: 'Team member created successfully.', type: 'success' });
      }
      if (onSuccess) onSuccess();
      closeModal();
      await fetchTeams();
    } catch (err) {
      const msg = err?.message || 'Something went wrong. Please try again.';
      setToast({ message: msg, type: 'error' });
      
      if (setError && err?.data?.field) {
        // Map backend field name to RHF field name if necessary
        setError(err.data.field, msg);
      } else if (setError && err?.errors) {
        // Existing support for validation object
        Object.entries(err.errors).forEach(([field, message]) => {
          setError(field, message);
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const tradeFilterOptions = useMemo(
    () => [
      { label: 'All types', value: 'All trades' },
      { label: 'Civil', value: 'civil' },
      { label: 'Electrical', value: 'electrical' },
      { label: 'Mechanical', value: 'mechanical' }
    ],
    []
  );

  const toggleMemberStatus = useCallback(
    async (member) => {
      if (!member?._id) return;
      const next = member.status === 'Active' ? 'Inactive' : 'Active';
      try {
        await teamService.updateEmployee(member._id, { status: next });
        setToast({
          message: next === 'Active' ? 'Member enabled.' : 'Member disabled.',
          type: 'success'
        });
        await fetchTeams();
      } catch (err) {
        setToast({ message: err?.message || 'Could not update status.', type: 'error' });
      }
    },
    [fetchTeams]
  );
//updated Halimunnisa 09-06-2026
// Added export functionality to allow users to export their teams list with applied filters, enhancing data management and usability for users with larger teams.
  const handleExport = async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      const { exportToExcel } = await import('@/utils/exportUtils');
      
      const params = {
        limit: 10000,
        search: debouncedSearchQuery,
        role: roleFilter,
        installationTrade: installationTradeFilter
      };

      let from = dateRange.from;
      let to = dateRange.to;

      if (datePreset) {
        const now = new Date();
        const todayStr = formatLocalYMD(now);
        to = todayStr;
        if (datePreset === 'today') {
          from = todayStr;
        } else if (datePreset === 'this_week') {
          const start = new Date(now);
          start.setDate(start.getDate() - 6);
          from = formatLocalYMD(start);
        } else if (datePreset === 'this_month') {
          const start = new Date(now);
          start.setDate(start.getDate() - 29);
          from = formatLocalYMD(start);
        } else if (datePreset === 'this_year') {
          const start = new Date(now);
          start.setDate(start.getDate() - 364);
          from = formatLocalYMD(start);
        }
      }

      if (from) params.startDate = from;
      if (to) params.endDate = to;

      setToast({ message: 'Fetching data for export...', type: 'info' });
      const response = await teamService.getEmployees(params);
      const allMembers = response?.data || [];

      if (allMembers.length === 0) {
        setToast({ message: 'No data to export', type: 'error' });
        return;
      }

      const exportData = allMembers.map(member => {
        const isAuditNoteLine = (line) => /^\[\d{4}-\d{2}-\d{2}T/.test(String(line).trim());
        const splitNotes = (raw) => {
          const lines = String(raw || '').split('\n').map((l) => l.trim()).filter(Boolean);
          return lines.filter((l) => !isAuditNoteLine(l)).join('\n');
        };
        const dateFmt = (d) => {
          if (!d) return '—';
          const dt = new Date(d);
          return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        };
        
        return {
          "Full Name": `${member.firstName || ''} ${member.lastName || ''}`.trim() || "—",
          "Employee ID": member.employeeId || "—",
          "Email Address": member.email || "—",
          "Phone Number": member.phone || "—",
          "Job Title": member.role || "—",
          "Installation Trade": member.role === 'field_officer' ? (member.installationTrade || "—") : "—",
          "Joining Date": dateFmt(member.joiningDate),
          "Work Location": member.workLocation || "—",
          "Department": member.department || 'Tower',
          "Login User ID": member.loginId || "—",
          "Status": member.status || "—",
          "Address": member.address || "—",
          "Administrative Notes": splitNotes(member.notes) || "—"
        };
      });

      await exportToExcel(exportData, `Teams_Export_${formatLocalYMD(new Date())}`);
      setToast({ message: 'Export completed successfully', type: 'success' });
    } catch (err) {
      console.error('Export failed:', err);
      setToast({ message: 'Failed to export team members.', type: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  return {
    refetchTeams: fetchTeams,
    showDetails,
    setShowDetails,
    searchQuery,
    handleSearch,
    roleFilter,
    handleRoleFilter,
    installationTradeFilter,
    handleInstallationTradeFilter,
    tradeFilterOptions,
    dateRange,
    datePreset,
    handleStartDateChange,
    handleEndDateChange,
    handleDatePreset,
    teamModalOpen,
    openCreateModal,
    openEditEmployee,
    closeModal,
    editingEmployee,
    viewingEmployee,
    setViewingEmployee,
    viewMode,
    setViewMode,
    isLoading,
    handleEmployeeSubmit,
    toggleMemberStatus,
    handlePageChange,
    employees,
    stats,
    page,
    totalPages,
    totalItems,
    error,
    toast,
    setToast,
    isExporting,
    handleExport
  };
};
